import type { Finding, FindingType, Importance, PlaybookCheck, ReviewStats } from './types'
import { t } from './i18n'

export const IMPORTANCE_ORDER: Importance[] = ['high', 'medium', 'low']

export function severityToImportance(
  severity: Finding['severity'] | string | null | undefined,
): Importance {
  if (severity === 'hard' || severity === 'high') return 'high'
  if (severity === 'preference' || severity === 'low') return 'low'
  if (severity === 'warning' || severity === 'medium') return 'medium'
  return 'medium'
}

export function findingImportance(finding: Finding): Importance {
  return finding.importance ?? severityToImportance(finding.severity)
}

export function findingAction(finding: Finding): 'addition' | 'revision' {
  if (finding.action) return finding.action
  return finding.finding_type === 'missing' ? 'addition' : 'revision'
}

export function findingSummary(finding: Finding): string {
  return finding.finding_summary || finding.change_summary || ''
}

export function evidenceQuotes(finding: Finding): string[] {
  const raw = finding.evidence
  const items = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : []
  return items
    .map((item) => (typeof item.quote === 'string' ? item.quote.trim() : ''))
    .filter(Boolean)
}

export type LabeledPart = { kind: 'label' | 'text'; value: string }

/** Labels from api/src/engine/compose.py + missing-clause summaries. Longest first. */
const TEXT_LABEL_PATTERN =
  'Closest rule|Suggested insert point|Approved wordings|Approved wording|Required clause is missing|Gap|Contract|Rule|' +
  'Правило \\(найближче\\)|Рекомендоване місце вставки|Затверджене формулювання|Затверджені формулювання|Відсутнє обов[\'ʼ’]язкове положення|Розбіжність|Договір|Правило|Джерело'

export function splitLabeledText(text: string): LabeledPart[] {
  if (!text) return []
  const parts: LabeledPart[] = []
  let last = 0
  const re = new RegExp(`(${TEXT_LABEL_PATTERN}):`, 'g')
  for (const match of text.matchAll(re)) {
    const index = match.index ?? 0
    if (index > last) {
      parts.push({ kind: 'text', value: text.slice(last, index) })
    }
    parts.push({ kind: 'label', value: match[0] })
    last = index + match[0].length
  }
  if (last < text.length) {
    parts.push({ kind: 'text', value: text.slice(last) })
  }
  return parts.length > 0 ? parts : [{ kind: 'text', value: text }]
}

export function isRecurring(finding: Finding): boolean {
  return finding.recurring === true || (finding.prior_finding_ids?.length ?? 0) >= 2
}

export function recurringNote(finding: Finding): string | null {
  if (!isRecurring(finding)) return null
  const count = finding.prior_finding_ids?.length ?? 0
  const dates = finding.prior_dates ?? []
  const head = count
    ? t('Це правило вже порушувалось у {count} інших договорах', { count })
    : t('Це правило вже порушувалось в інших договорах')
  return dates.length ? `${head} (${dates.join(', ')}).` : `${head}.`
}

export function checkCanNavigate(check: PlaybookCheck): boolean {
  return (check.anchor_text ?? '').trim().length > 0
}

export function checkLocationLabel(check: PlaybookCheck): string | null {
  if (check.insert_mode === 'insert' && (check.section_path ?? '').trim()) {
    return t('вставка біля {section}', { section: check.section_path ?? '' })
  }
  const ref = (check.clause_ref ?? '').trim()
  if (ref) return ref
  const section = (check.section_path ?? '').trim()
  return section || null
}

export function isBlankPlaybookCheck(check: PlaybookCheck): boolean {
  const title = (check.title ?? '').trim()
  const text = (check.text ?? '').replace(/[^\p{L}\p{N}]+/gu, '')
  if (text) return false
  return title.length === 0 || /^(без назви|untitled|no title)$/i.test(title)
}

export function checkStatusLabel(status: PlaybookCheck['status']): string {
  switch (status) {
    case 'met':
      return t('Підтверджено')
    case 'unmet':
      return t('Порушено')
    case 'partial':
      return t('Частково')
    case 'not_applicable':
      return t('Не застосовується')
    case 'needs_review':
      return t('Потребує перевірки')
    case 'error':
      return t('Помилка перевірки')
    case 'not_evaluated':
      return t('Не підтверджено')
  }
}

export function countByImportance(findings: Finding[]): Record<Importance, number> {
  const out: Record<Importance, number> = { high: 0, medium: 0, low: 0 }
  for (const f of findings) {
    out[findingImportance(f)] += 1
  }
  return out
}

export function formatReviewReport(params: {
  findings: Finding[]
  checks?: PlaybookCheck[]
  stats?: ReviewStats
}): string {
  const lines = [t('# Звіт ревʼю EUDOKIA'), '']
  const s = params.stats
  if (s?.review_complete === false) lines.push(t('Увага: ревю неповне; є неперевірені правила.'), '')
  if (s) {
    lines.push(
      t('Відхилень: {t1}, відсутніх: {t2}, нетипових: {t3}, повторів: {t4}.', {
        t1: s.t1 ?? 0,
        t2: s.t2 ?? 0,
        t3: s.t3 ?? 0,
        t4: s.t4 ?? 0,
      }),
      '',
    )
  }
  if (params.checks?.length) {
    lines.push(`## ${t('Плейбук')}`)
    for (const c of params.checks) {
      const title = c.title || c.text.slice(0, 80)
      const status = checkStatusLabel(c.status).toLowerCase()
      const loc = checkLocationLabel(c)
      lines.push(
        `- ${status} [${importanceLabel(c.importance)}] ${title}${loc ? ` — ${loc}` : ''}`,
      )
    }
    lines.push('')
  }
  const fixes = params.findings.filter((f) => f.finding_type !== 'atypical')
  const atypical = params.findings.filter((f) => f.finding_type === 'atypical')
  appendFindingSection(lines, t('Правки'), fixes)
  appendFindingSection(lines, t('Нетипове'), atypical)
  return lines.join('\n')
}

function appendFindingSection(lines: string[], heading: string, items: Finding[]): void {
  if (items.length === 0) return
  lines.push(`## ${heading}`)
  for (const f of items) {
    const loc = f.clause_ref ?? f.section_path ?? t('без привʼязки')
    lines.push(`### ${loc} — ${typeLabel(f.finding_type)} (${importanceLabel(findingImportance(f))})`)
    lines.push(findingSummary(f))
    if (f.justification) lines.push(f.justification)
    lines.push('')
  }
}

export function typeLabel(type: FindingType): string {
  switch (type) {
    case 'deviation':
      return t('Відхилення')
    case 'missing':
      return t('Відсутнє')
    case 'atypical':
      return t('Нетипове')
    case 'recurring':
      return t('Повтор')
  }
}

export function importanceLabel(level: Importance): string {
  switch (level) {
    case 'high':
      return t('Висока')
    case 'medium':
      return t('Середня')
    case 'low':
      return t('Низька')
  }
}


export function checkReasonLabel(reason?: string): string {
  const labels: Record<string, string> = {
    model_or_evidence_error: t('Не вдалося отримати відповідь із підтвердженими цитатами.'),
    ambiguous_or_unverified: t(
      'Доказів недостатньо або незалежна перевірка не підтвердила висновок.',
    ),
    cross_window_reconciliation_required: t('Потрібне узгодження умов між частинами документа.'),
    deadline_exceeded: t('Вичерпано час перевірки.'),
    cost_budget_exceeded: t('Досягнуто ліміту витрат на перевірку.'),
    call_budget_exceeded: t('Досягнуто ліміту запитів.'),
    model_price_unknown: t('Не налаштовано тариф моделі для контролю витрат.'),
    numeric_context_ambiguous: t('Неоднозначні число, одиниця або умова відліку.'),
    conflicting_playbook_rules: t('У плейбуку знайдено суперечливі правила.'),
    absent_after_full_scan: t('Перевірено весь доступний текст документа.'),
    empty_document: t('Документ не містить тексту для перевірки.'),
    empty_rule_text: t('У правила немає тексту вимоги — ревʼю не може його шукати.'),
  }
  return reason ? (labels[reason] ?? t('Потребує ручної перевірки.')) : ''
}
